import React from 'react';
import { useProjectStore } from '../../hooks/useProjectStore';
import { useMechProfile, MTH } from './mechShared';
import DepthProfile, { Track } from './DepthProfile';

// /mechanics/profile — 力学参数剖面 (E / ν / UCS / 脆性)
export default function MechProfilePage() {
  const { currentProject } = useProjectStore();
  const { data, loading } = useMechProfile(currentProject?.id);
  const sourceSummary = React.useMemo(() => {
    const hasDynamic = (point: (typeof data)[number]) => point.dynamic_youngs_modulus != null || point.dynamic_poissons_ratio != null;
    const hasEngineering = (point: (typeof data)[number]) => [
      point.youngs_modulus, point.poissons_ratio, point.ucs, point.tensile_strength,
      point.friction_angle, point.cohesion, point.brittleness_index,
      point.sigma_v, point.sigma_h, point.sigma_h_max, point.stress_direction,
    ].some((value) => value != null);
    return {
      logDynamic: data.filter((point) => hasDynamic(point) && point.dynamic_calculation_source === 'datahub_dynamic_logs').length,
      otherDynamic: data.filter((point) => hasDynamic(point) && point.dynamic_calculation_source !== 'datahub_dynamic_logs').length,
      engineering: data.filter(hasEngineering).length,
      unlabelledEngineering: data.filter((point) => hasEngineering(point) && !point.calculation_source).length,
    };
  }, [data]);

  const tracks: Track[] = [
    { title: '动态杨氏模量 Ed（来源见上方）', unit: 'GPa', min: 0, max: 100, series: [{ label: 'Ed', color: '#2874A6', accessor: (p) => p.dynamic_youngs_modulus }] },
    { title: '动态泊松比 νd（来源见上方）', unit: '', min: 0.1, max: 0.4, series: [{ label: 'νd', color: '#6A4C93', accessor: (p) => p.dynamic_poissons_ratio }] },
    { title: '静态/工程杨氏模量 Es（非测井直算）', unit: 'GPa', min: 0, max: 60, series: [{ label: 'Es', color: '#1E8449', accessor: (p) => p.youngs_modulus }] },
    { title: '静态/工程泊松比 νs（非测井直算）', unit: '', min: 0.1, max: 0.4, series: [{ label: 'νs', color: '#AD1457', accessor: (p) => p.poissons_ratio }] },
    { title: '抗压强度 UCS', unit: 'MPa', min: 0, max: 250, series: [{ label: 'UCS', color: '#1E8449', accessor: (p) => p.ucs }] },
    { title: '脆性指数 BI', unit: '', min: 0, max: 1, series: [{ label: 'BI', color: '#C2533A', accessor: (p) => p.brittleness_index }] },
  ];

  if (!currentProject) return <div style={{ padding: 40, color: MTH.textSec }}>请先选择项目</div>;

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: MTH.bg, fontFamily: 'inherit' }}>
      <div style={{ padding: '8px 20px', borderBottom: `1px solid ${MTH.headerBorder}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <h2 style={{ fontSize: 15, fontWeight: 700, color: MTH.text, margin: 0 }}>力学参数剖面</h2>
          <span style={{ fontSize: 12, color: MTH.textMuted }}>
            {loading ? '加载中…' : `${data.length.toLocaleString()} 个深度点`}
          </span>
        </div>
        {!loading && data.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginTop: 7, fontSize: 11 }}>
            <span style={{ padding: '3px 7px', borderRadius: 3, color: '#1E5F8A', background: '#EAF4FB' }}>
              测井数据动态试算 Ed / νd（待验证）：{sourceSummary.logDynamic.toLocaleString()} 点
            </span>
            {sourceSummary.otherDynamic > 0 && (
              <span style={{ padding: '3px 7px', borderRadius: 3, color: '#6A4C93', background: '#F2ECF8' }}>
                其它动态参数：{sourceSummary.otherDynamic.toLocaleString()} 点
              </span>
            )}
            <span style={{ padding: '3px 7px', borderRadius: 3, color: '#246B3D', background: '#EAF6EE' }}>
              静态/工程输入：{sourceSummary.engineering.toLocaleString()} 点
            </span>
            {sourceSummary.unlabelledEngineering > 0 && (
              <span style={{ padding: '3px 7px', borderRadius: 3, color: '#8A5A00', background: '#FFF5D6' }}>
                其中旧数据来源未标注：{sourceSummary.unlabelledEngineering.toLocaleString()} 点
              </span>
            )}
            <span style={{ color: MTH.textMuted }}>来源标记为 datahub_dynamic_logs 的 Ed、νd 是 DEN + 纵/横波的初步试算结果，尚未完成静态转换和专业标定，不属于 DataHub v2.0 正式计算能力；Es、νs、UCS、强度和地应力来自导入或工程标定。</span>
          </div>
        )}
      </div>
      <div style={{ flex: 1, minHeight: 0, padding: '4px 8px' }}>
        {!loading && data.length === 0
          ? <div style={{ padding: 40, color: MTH.textMuted, textAlign: 'center' }}>无岩石力学数据 — 后续可从数据中心导入已有工程参数或接入正式计算结果</div>
          : <DepthProfile points={data} tracks={tracks} />}
      </div>
    </div>
  );
}
